import { useMemo, useState, type ReactNode } from "react";
import { useMediaQuery } from "@reactuses/core";
import { Col, Row } from "react-bootstrap";
import SummaryCard from "../../core/blocks/SummaryCard";
import Metric from "../../core/blocks/SummaryMetric";
import useSelectedMetrics from "./hooks/useSelectedMetrics";
import MultiMetricChart from "./MultiMetricChart";
import { saturateColor } from "../../../utils/colors";
import { LegacyMetricSelector } from "../../core/blocks/MetricSelector";
import { useStandardEntityMetrics } from "../../../modules/merchandising/components/tables/metricUtils";
import getMetricSpec, { metricNameSelector, metricToolTipSelector } from "../../../modules/merchandising/utils/metrics";
import { useTranslation } from "react-i18next";
import { useAppSelector } from "../../../redux/hooks";
import { ButtonGroup } from "../../core/basic/ButtonGroup";
import moment from "moment";
import { useDateFormatter } from "../../../i18n/useDateFormatter";
import { FaTable } from "react-icons/fa6";
import { IoStatsChart } from "react-icons/io5";
import type { SeriesLegendItemClickEventObject } from "highcharts/highcharts.src";
import { css } from "../../../../styled-system/css";
import { FaEyeSlash } from "react-icons/fa";
import Card from "../../core/basic/Card";
import { Flex, Wrap } from "../../../../styled-system/jsx";
import type { MetricConfig } from "../../../modules/merchandising/utils/metrics";
import { Table } from "../../table/Table";
import { RT8Pagination } from "../../table/Pagination";
import { createColumnBuilder } from "../../table/columnBuilder";
import { usePersistedTableState } from "../../table/usePersistedTableState";
import { useClientTable } from "../../table/tableFeatures";
const columnHelper = createColumnBuilder<Record<string, unknown> & { start_date: string }>();

const setShownMetrics = (key: string, columns: string[]) => {
  const options = localStorage.getItem("metricShowOptions");
  let parsedOptions = {};
  try {
    parsedOptions = JSON.parse(options!) as Record<string, string[]>;
  } catch {
    // noop
  }
  localStorage.setItem("metricShowOptions", JSON.stringify({ ...parsedOptions, [key]: columns }));
};

const getShownMetrics = (key: string, defaults?: string[]) => {
  const options = localStorage.getItem("metricShowOptions");
  defaults = defaults || [];
  if (options) {
    try {
      return (JSON.parse(options) as Record<string, string[]> | null)?.[key] ?? defaults;
    } catch {
      return defaults;
    }
  }
  return defaults;
};

interface Props<T> {
  totals?: { data: T | undefined; loading?: boolean };
  timeline?: { data: (T & { start_date: string })[] | undefined; loading?: boolean };
  id?: string;
  title?: string;
  titleElement?: ReactNode;
  byline: ReactNode;
  metrics: Array<MetricConfig<T>>;
  initialSelectedMetric: string[] | string;
  children?: ReactNode;
  group?: string;
  showMetricsSelector?: boolean;
  inlineStatus?: ReactNode;
  selectedCategory?: null;
  selectedAd?: null;
  selectedProduct?: null;
  showAnnotations?: boolean;
  context?: string;
  extendMetrics?: boolean;
  cellHighlightKey?: string | null;
  cellHighlightDates?: string[] | null;
  marketPlace?: string;
}

const MetricsCard = <T,>({
  id,
  title,
  titleElement,
  byline,
  totals,
  timeline,
  metrics,
  initialSelectedMetric,
  children,
  group = "day",
  showMetricsSelector = false,
  inlineStatus,
  selectedCategory = null,
  selectedAd = null,
  selectedProduct = null,
  showAnnotations = false,
  context,
  extendMetrics = true,
  cellHighlightKey = null,
  cellHighlightDates = null,
  marketPlace: marketPlaceOverride,
}: Props<T>) => {
  const mobileStyles = useMediaQuery("(max-width:600px)");
  const { t } = useTranslation(["merchandising", "metrics"]);
  const monthYearFmt = useDateFormatter("monthYear");
  const shortFmt = useDateFormatter("short");
  const { selectedMetrics, toggleMetric, setSelectedMetrics } = useSelectedMetrics(
    Array.isArray(initialSelectedMetric) ? initialSelectedMetric : [initialSelectedMetric],
    id,
  );

  const [chartToggle, setChartToggle] = useState("chart");

  let marketPlace = useAppSelector((state) => state.marketPlace.marketPlace);
  marketPlace = marketPlaceOverride ?? marketPlace;

  const standardMetricIds = useStandardEntityMetrics(context);
  const chartMetrics = useMemo((): Array<MetricConfig<T> & { isPrev?: boolean }> => {
    if (!extendMetrics) {
      return metrics;
    }

    // Start with manually passed metrics
    const allMetrics = [...metrics];
    const existingIds = new Set(metrics.map((m) => m.id));

    // Add standard metrics that aren't already included
    for (const metricId of standardMetricIds) {
      if (existingIds.has(metricId)) {
        continue;
      }
      const metricSpec = getMetricSpec<T>(metricId);
      allMetrics.push(metricSpec);
    }

    // Add "previous" variants for metrics with prev_accessor.
    // Display labels for synthetic _prev ids resolve inside MultiMetricChart and the
    // column selector below via `isPrev` + base id, since the registry's byId map
    // doesn't cover every synthetic _prev variant.
    return allMetrics.reduce(
      (acc, metric) => {
        acc.push(metric);
        if (metric.prev_accessor) {
          acc.push({
            ...metric,
            id: `${metric.id}_prev`,
            isPrev: true,
            accessor: metric.prev_accessor,
            color: saturateColor(metric.color, 1.5),
          });
        }
        return acc;
      },
      [] as Array<MetricConfig<T> & { isPrev?: boolean }>,
    );
  }, [metrics, standardMetricIds, extendMetrics]);

  const [showMetrics, setShowMetrics] = useState(() => {
    const initial = chartMetrics
      .filter((m) => !m.isPrev)
      .slice(0, 6)
      .map((metric) => metric.id);
    if (id) {
      return getShownMetrics(id, initial);
    }
    return initial;
  });

  const filteredMetrics = useMemo(() => {
    return showMetrics.map((id) => chartMetrics.find((metric) => metric.id === id)).filter(Boolean);
  }, [chartMetrics, showMetrics]);

  const orderedChartMetrics = useMemo(() => {
    const shown = showMetrics.map((id) => chartMetrics.find((m) => m.id === id)).filter(Boolean);
    const rest = chartMetrics.filter((m) => !showMetrics.includes(m.id));

    return [...shown, ...rest];
  }, [chartMetrics, showMetrics]);

  const handleLegendChange = (e: SeriesLegendItemClickEventObject) => {
    const id = e.target.options.custom?.id;
    if (id) toggleMetric(id);
    return false;
  };

  const onShowMetricsChange = (showMetrics: string[]) => {
    setShowMetrics(showMetrics);
    if (id) {
      setShownMetrics(id, showMetrics);
    }
  };

  const columnSpecs = useMemo(() => {
    return filteredMetrics.filter((m) => !m.isPrev).map((m) => m.id);
  }, [filteredMetrics]);

  const tableColumns = useMemo(() => {
    return [
      columnHelper.accessor((row) => row.start_date, {
        id: "start_date",
        header: t(($) => $.merchandising["Date"]),
        cell: ({ getValue }) => {
          const fmt = group === "month" ? monthYearFmt : shortFmt;
          return fmt.format(moment(getValue()).toDate());
        },
        size: 150,
        minSize: 150,
        enableColumnFilter: false,
      }),
      ...columnSpecs.map((metricId) => columnHelper.metric(metricId)),
    ];
  }, [columnSpecs, group, t, monthYearFmt, shortFmt]);

  const { state: tableState, setters: tableSetters } = usePersistedTableState({
    persistKey: `${context}TimelineTable`,
    initialState: {
      sorting: [{ id: "start_date", desc: false }],
      pagination: { pageIndex: 0, pageSize: 10 },
    },
  });

  const table = useClientTable({
    data: timeline?.data,
    columns: tableColumns,
    state: tableState,
    ...tableSetters,
  });

  const toggleButtons = [
    {
      value: "chart",
      label: <IoStatsChart />,
    },
    {
      value: "table",
      label: <FaTable />,
    },
    {
      value: "hide",
      label: <FaEyeSlash />,
    },
  ];

  return (
    <>
      <Row
        className={css({
          paddingY: "small",
          overflowX: "auto",
          flexWrap: "nowrap",
          ".container-fluid &": { marginBottom: "small" },
        })}
        hidden={!totals}
      >
        {filteredMetrics
          .filter((m) => !m.isPrev)
          .map((m) => {
            return (
              <Col
                key={m.id}
                className={css({
                  cursor: "pointer",
                  flex: "0 0 auto",
                  width: {
                    base: "50%",
                    lg: "25%",
                    xl: "16.6%",
                  },
                })}
                onClick={() => {
                  toggleMetric(m.id);
                }}
              >
                <Card className={css({ padding: "medium" })}>
                  <Metric
                    metric={m}
                    data={totals?.data}
                    showTarget={!!m.prev_accessor}
                    selected={selectedMetrics.includes(m.id)}
                    loading={totals?.loading ?? false}
                  />
                </Card>
              </Col>
            );
          })}
      </Row>
      <SummaryCard
        title={titleElement ? titleElement : title}
        byline={byline}
        status={
          showMetricsSelector && (
            <Wrap gap="small" justifyContent="end" alignItems="center">
              {inlineStatus}
              <ButtonGroup
                variant="inline"
                buttons={toggleButtons}
                onSelect={(value) => {
                  setChartToggle(value);
                }}
                value={chartToggle}
              />
              <LegacyMetricSelector
                groupMetrics={true}
                entityType={context}
                selectorType="overview"
                columnOptions={orderedChartMetrics.map((m) => {
                  const baseId = m.isPrev && m.id.endsWith("_prev") ? m.id.slice(0, -"_prev".length) : m.id;
                  const baseName =
                    m.name == null ? t(metricNameSelector(baseId), { defaultValue: baseId })
                    : typeof m.name === "string" ? m.name
                    : t(m.name);
                  const label =
                    m.isPrev ? t(($) => $.merchandising["{name} (Previous)"], { name: baseName }) : baseName;
                  const toolTip =
                    m.toolTip == null ? t(metricToolTipSelector(baseId), { defaultValue: "" })
                    : typeof m.toolTip === "string" ? m.toolTip
                    : t(m.toolTip);
                  return {
                    ...m,
                    label,
                    toolTip: toolTip || undefined,
                    checked: showMetrics.includes(m.id),
                  };
                })}
                onMetricChange={(updatedMetric) => {
                  const currentShownMetrics = new Set(showMetrics);

                  const nextShownMetrics = Object.entries(updatedMetric)
                    .filter(([, show]) => show)
                    .map(([metricId]) => metricId);

                  const netNewMetrics = new Set(nextShownMetrics).difference(currentShownMetrics);

                  setSelectedMetrics((currentSelectedMetrics) => {
                    return [...currentSelectedMetrics, ...Array.from(netNewMetrics)];
                  });
                  onShowMetricsChange(nextShownMetrics);
                }}
                onResetMetrics={() => {
                  setShowMetrics([]);
                  const defaultShowMetrics = chartMetrics
                    .filter((m) => !m.isPrev)
                    .slice(0, 6)
                    .map((metric) => metric.id);
                  onShowMetricsChange(defaultShowMetrics);
                }}
                setSavedColumnOrder={(updatedMetrics) => {
                  if (!id) {
                    return;
                  }
                  const updatedOrder = Object.keys(updatedMetrics).sort((a, b) => {
                    if (updatedMetrics[a] && updatedMetrics[b]) {
                      return updatedMetrics[a] - updatedMetrics[b];
                    }
                    return 0;
                  });
                  setShownMetrics(id, updatedOrder);
                  setShowMetrics(updatedOrder);
                }}
              />
            </Wrap>
          )
        }
      >
        {chartToggle === "chart" && timeline?.data && (
          <MultiMetricChart
            handleLegendChange={handleLegendChange}
            selectedMetrics={selectedMetrics}
            selectedCategory={selectedCategory}
            selectedAd={selectedAd}
            selectedProduct={selectedProduct}
            metrics={filteredMetrics}
            data={timeline.data}
            mobileStyles={mobileStyles}
            size={"normal"}
            group={group}
            loading={timeline.loading ?? false}
            showAnnotations={showAnnotations}
            cellHighlightKey={cellHighlightKey}
            cellHighlightDates={cellHighlightDates}
            marketPlace={marketPlace}
          />
        )}
        {chartToggle === "table" && timeline?.data && (
          <>
            <Flex justifyContent="end">
              <RT8Pagination table={table} />
            </Flex>
            <Table table={table} />
            <Flex justifyContent="end">
              <RT8Pagination table={table} />
            </Flex>
          </>
        )}
        {children}
      </SummaryCard>
    </>
  );
};

export default MetricsCard;
